import { useState } from "react";
import { Link } from "react-router";
import { Btn, Empty, ErrorNote, Loading, PageHead, Section } from "../components/kit";
import { get, type AuditEntry } from "../lib/api";
import { ago, describeAction, formatDateTime } from "../lib/format";
import { useResource } from "../lib/hooks";

function targetLink(e: AuditEntry) {
  if (!e.target_id) return null;
  if (e.target_kind === "account") return `/accounts/${e.target_id}`;
  if (e.target_kind === "client") return `/portals/${e.target_id}`;
  return null;
}

/** Entries from the record, newest first. */
export function AuditList({ entries }: { entries: AuditEntry[] }) {
  if (entries.length === 0) return <Empty title="Nothing has changed yet." />;
  return (
    <ol role="list" className="timeline">
      {entries.map((e) => {
        const to = targetLink(e);
        return (
          <li key={e.id}>
            <div className="timeline-what">
              <strong>{describeAction(e.action)}</strong>
              {e.detail && <span className="muted"> · {e.detail}</span>}
              {to && (
                <>
                  {" "}
                  <Link className="link" to={to}>
                    Open
                  </Link>
                </>
              )}
            </div>
            <div className="timeline-meta">
              <span>{e.actor}</span>
              <time dateTime={e.created_at} title={formatDateTime(e.created_at)}>
                {ago(e.created_at)}
              </time>
              {e.ip && <span className="code">{e.ip}</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function AuditPage() {
  const first = useResource<{ entries: AuditEntry[]; more: boolean }>("/audit");
  const [older, setOlder] = useState<AuditEntry[]>([]);
  const [more, setMore] = useState<boolean | null>(null);
  const entries = [...(first.data?.entries ?? []), ...older];
  const hasMore = more ?? first.data?.more ?? false;

  const loadMore = async () => {
    const last = entries[entries.length - 1];
    if (!last) return;
    const page = await get<{ entries: AuditEntry[]; more: boolean }>(`/audit?before=${encodeURIComponent(last.created_at)}`);
    setOlder((o) => [...o, ...page.entries]);
    setMore(page.more);
  };

  return (
    <>
      <PageHead
        title="Audit log"
        lede="Every change made in this panel, with the administrator who made it. Nothing here can be edited or deleted."
      />
      <ErrorNote error={first.error} />
      <Section title="Record" aside={first.data && <span className="muted small">Newest first</span>}>
        {first.data ? <AuditList entries={entries} /> : <Loading />}
        {hasMore && (
          <div className="more">
            <Btn onClick={loadMore}>Show older</Btn>
          </div>
        )}
      </Section>
    </>
  );
}
